import { useEffect, useState } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { RequireAuth, signOut } from 'aws-auth-lib';
import { useDispatch } from 'react-redux';
import { useRole } from '../utils/RoleContext';
import { getUser } from '../services/users';
import { resetUser, updateRole, updateUser } from '../store/userReducer';
import { useUser } from '../hooks/useUser';
import Loader from '../components/Loader';
import { canAccessWebPlatform } from '../permissions';

const ProtectedRoute = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { setRole } = useRole();
    const { user } = useUser();
    const [loadingUser, setLoadingUser] = useState(true);

    useEffect(() => {
        let mounted = true;
        getUser()
            .then((response) => {
                if (!mounted) return;
                const data = response?.data;
                if (data) {
                    dispatch(updateUser(data));
                }
            })
            .catch(async (error) => {
                if (!mounted) return;
                if (error?.response?.status === 401) {
                    try {
                        await signOut();
                    } catch {
                        // best effort
                    }
                    dispatch(resetUser());
                    sessionStorage.removeItem('role');
                }
                navigate('/login', { replace: true });
            })
            .finally(() => {
                if (!mounted) return;
                setLoadingUser(false);
            });

        return () => {
            mounted = false;
        };
    }, [dispatch, navigate]);

    useEffect(() => {
        if (!loadingUser && user.roles?.length > 0 && !user.selectedRole) {
            if (user.roles.length === 1) {
                const role = user.roles[0];
                dispatch(updateRole(role.uuid));
                const roleForPermission = role.role || role.name;
                sessionStorage.setItem('role', roleForPermission);
                setRole(roleForPermission);
                if (!canAccessWebPlatform(roleForPermission)) {
                    navigate('/restricted', { replace: true });
                }
            } else {
                navigate('/role', { state: { roles: user.roles } });
            }
        }
    }, [dispatch, loadingUser, navigate, setRole, user]);

    useEffect(() => {
        if (!loadingUser && user.selectedRole && user.roles?.length > 0) {
            const currentRole = user.roles.find(
                (role) => role.uuid === user.selectedRole
            );
            if (currentRole) {
                const roleForPermission = currentRole.role || currentRole.name;
                sessionStorage.setItem('role', roleForPermission);
                setRole(roleForPermission);
                if (!canAccessWebPlatform(roleForPermission)) {
                    navigate('/restricted', { replace: true });
                }
            }
        }
    }, [loadingUser, navigate, setRole, user]);

    const action = () => {
        return <Navigate to="/login" replace />;
    };

    return (
        <RequireAuth action={action}>
            {loadingUser ? <Loader /> : <Outlet />}
        </RequireAuth>
    );
};

export default ProtectedRoute;
